/*
 * Programmatic routes definitions.
 * Keep separate from rest of app and pass deps via arguments
 *  so it can be required during build.
 * https://github.com/reactjs/react-router/blob/master/docs/API.md#plainroute
 */
import builders from './builders';

// Composer function to build all routes by passing all functions into funcs
// Concatenates the routes and passes them into each following function as an argument
function buildAllRoutes({ routes, obj, funcs }) {
  return funcs.reduce((acc, func) => {
    return acc.concat(func(Object.assign({ routes }, obj)));
  }, routes);
}

// Function to construct the root path's child routes for web
function buildRootChildRoutes({ builderObj }) {
  const INITIAL_ROUTES = [];
  return buildAllRoutes({
    routes: INITIAL_ROUTES,
    obj: builderObj,
    funcs: [
      builders.domains.payments.buildUpgradeRoutes,
      builders.domains.playback.buildPlaybackRoutes,
      builders.domains.collection.buildCollectionRoutes,
      builders.domains.discovery.buildDiscoveryRoutes,
      builders.domains.listener.buildSocialRoutes,
      builders.domains.payments.buildSettingsRoutes,
      builders.domains.search.buildSearchRoutes,
      builders.domains.backstage.buildBackstageRoutes,
      builders.domains.payments.buildMiscResponsivePaymentsRoutes,
      builders.domains.listener.buildDesktopRoutes,
      builders.core.buildErrorRoutes,
    ],
  });
}

// Function to construct the root path's child routes for mobileWeb
function buildRootChildRoutesMobileWeb({ builderObj }) {
  const INITIAL_ROUTES = [];
  const funcs = [
    builders.domains.listener.buildAccountRoutes,
    builders.domains.payments.buildUpgradeRoutes,
    builders.domains.discovery.buildDiscoveryRoutes,
    builders.domains.payments.buildMobileSettingsRoutes,
    builders.domains.payments.buildMiscResponsivePaymentsRoutes,
    builders.core.buildErrorRoutes,
  ];

  // Exclude backstage routes for tablet devices
  const isMobileWeb = builderObj.isMobileWeb;
  if (isMobileWeb) {
    funcs.unshift(builders.domains.backstage.buildBackstageRoutes);
  }

  return buildAllRoutes({
    routes: INITIAL_ROUTES,
    obj: builderObj,
    funcs,
  });
}

// Builds root route with childpaths ('/') for either mobile web or web depending
// on isMobileWeb and isTablet variable sent from mobileRoutes
function buildRootRoute({
  routes,
  isMobileWeb,
  isTablet,
  onEnterAllSongsNowPlayingShortRoute,
  onEnterBackstage,
  onLeaveBackstage,
  AsyncDomain,
  redirectToStationDetails,
  onEnterMyStations,
  onEnterNowPlayingOnDemand,
  onEnterPodcastNowPlaying,
  onLeaveNowPlaying,
  onEnterNowPlaying,
  onEnterNowPlayingSessionHistory,
  onEnterMyMusic,
  onEnterCollection,
  onBrowseCatalog,
  errorLoading,
  redirectToGenreBackstage,
  onEnterProfile,
  createConditionalComponent,
  userProfile2Enabled,
  onEnterSettings,
  onEnterBackstageLyrics,
  onEnterSubscriptionManagement,
  isResponsiveSubscriptionEnabled,
  onEnterFamilyPlanManagement,
  onEnterUpgradeFlow,
  onChangeUpgradeFlow,
  onEnterPartnerUpgradeFlow,
  redirectToPartnerUpgrade,
  onMountSearchT3,
  onEnterPodcastBSP,
  onEnterCollectedStationBSP,
  onEnterCuratorBSP,
  onLeaveCuratorBSP,
  isCuratorExperienceEnabled,
  onEnterPlaylist,
  onLeavePlaylist,
  hidePodcastCollection,
  onEnter404,
  onLeave404,
  onEnterLanding,
  onChangeLanding,
  AppContainer,
  redirectPlaybackToBackstage,
  onEnterBrowse,
  onEnterPodcastBrowse,
  isLoggedInUser,
  redirectIfAuthenticated,
  redirectIfAuthenticatedOnChange,
  redirectIfAuthenticatedOrBlocked,
  onEnterMobileAccountWelcome,
  redirectToMyMusic,
  onEnterStationLanding,
  onEnterFirstIntro,
  onEnterVerification,
  onEnterMobileMessage,
  onEnterPlans,
  onEnterPaypalCancel,
  onEnterPaypalComplete,
  Loading,
}) {
  let rootChildRoutes;
  if (isMobileWeb || isTablet) {
    rootChildRoutes = buildRootChildRoutesMobileWeb({
      builderObj: {
        AsyncDomain,
        onEnterBackstage,
        onEnterPodcastBSP,
        onEnterCuratorBSP,
        onLeaveCuratorBSP,
        isCuratorExperienceEnabled,
        onEnterPlaylist,
        onLeavePlaylist,
        createConditionalComponent,
        isMobileWeb,
        redirectPlaybackToBackstage,
        errorLoading,
        onEnter404,
        onLeave404,
        redirectIfAuthenticated,
        redirectIfAuthenticatedOnChange,
        redirectIfAuthenticatedOrBlocked,
        onEnterMobileAccountWelcome,
        onEnterSettings,
        onEnterBackstageLyrics,
        onEnterSubscriptionManagement,
        isResponsiveSubscriptionEnabled,
        isTablet,
        redirectToMyMusic,
        onEnterUpgradeFlow,
        onChangeUpgradeFlow,
        isLoggedInUser,
        onEnterStationLanding,
        onEnterFirstIntro,
        onEnterFamilyPlanManagement,
        redirectToPartnerUpgrade,
        onEnterPartnerUpgradeFlow,
        onEnterMobileMessage,
        onEnterVerification,
        onEnterPlans,
        onEnterPaypalCancel,
        onEnterPaypalComplete,
        onEnterBrowse,
        Loading,
      },
    });
  } else {
    rootChildRoutes = buildRootChildRoutes({
      builderObj: {
        onEnterAllSongsNowPlayingShortRoute,
        onEnterBackstage,
        onLeaveBackstage,
        AsyncDomain,
        redirectToStationDetails,
        onEnterMyStations,
        onEnterNowPlayingOnDemand,
        onEnterPodcastNowPlaying,
        onLeaveNowPlaying,
        onEnterNowPlaying,
        onEnterNowPlayingSessionHistory,
        onEnterMyMusic,
        onEnterCollection,
        onBrowseCatalog,
        onEnterBrowse,
        onEnterPodcastBrowse,
        errorLoading,
        redirectToGenreBackstage,
        onEnterProfile,
        createConditionalComponent,
        userProfile2Enabled,
        onEnterSettings,
        onEnterBackstageLyrics,
        onEnterSubscriptionManagement,
        onEnterFamilyPlanManagement,
        onEnterUpgradeFlow,
        onChangeUpgradeFlow,
        onEnterPartnerUpgradeFlow,
        redirectToPartnerUpgrade,
        onMountSearchT3,
        onEnterPodcastBSP,
        onEnterCollectedStationBSP,
        onEnterCuratorBSP,
        onLeaveCuratorBSP,
        isCuratorExperienceEnabled,
        onEnterPlaylist,
        onLeavePlaylist,
        hidePodcastCollection,
        onEnter404,
        onLeave404,
        isLoggedInUser,
        onEnterVerification,
        onEnterMobileMessage,
        onEnterPlans,
        onEnterPaypalCancel,
        onEnterPaypalComplete,
        Loading,
      },
    });
  }

  return routes.concat([
    {
      path: '/',
      onEnter: onEnterLanding,
      onChange: onChangeLanding,
      component: AppContainer,
      getIndexRoute(location, cb) {
        System.import(/* webpackChunkName: "c_onboarding" */ '../components/Onboarding/Onboarding')
          .then(module => {
            cb(null, { component: module.default });
          })
          .catch(errorLoading);
      },
      childRoutes: rootChildRoutes,
    },
  ]);
}

// Builds the routes for the web-client
export function webRouteBuilder({ builderObj }) {
  const INITIAL_ROUTES = [];
  return buildAllRoutes({
    routes: INITIAL_ROUTES,
    obj: builderObj,
    funcs: [
      builders.core.buildLegacyRoutes,
      builders.core.buildAnonymousRoutes,
      builders.domains.listener.buildAccountRoutes,
      buildRootRoute,
    ],
  });
}

// Builds the routes for the mobile web-client
export function mobileRouteBuilder({
  isMobileWeb,
  isTablet,
  onEnterBackstage,
  AsyncDomain,
  errorLoading,
  onEnterLanding,
  onChangeLanding,
  AppContainer,
  onEnterPodcastBSP,
  onEnterCuratorBSP,
  onLeaveCuratorBSP,
  isCuratorExperienceEnabled,
  onEnterPlaylist,
  onLeavePlaylist,
  createConditionalComponent,
  redirectPlaybackToBackstage,
  redirectIfAuthenticated,
  redirectIfAuthenticatedOnChange,
  redirectIfAuthenticatedOrBlocked,
  onEnterMobileAccountWelcome,
  onEnterSettings,
  onEnterBackstageLyrics,
  onEnterSubscriptionManagement,
  isResponsiveSubscriptionEnabled,
  isLoggedInUser,
  onEnterUpgradeFlow,
  onChangeUpgradeFlow,
  onEnterFamilyPlanManagement,
  redirectToPartnerUpgrade,
  onEnterPartnerUpgradeFlow,
  onEnterMobileMessage,
  onEnterVerification,
  onEnterPlans,
  onEnterPaypalCancel,
  onEnterPaypalComplete,
  onEnterBrowse,
}) {
  const mobileWebRoutes = buildRootRoute({
    routes: [],
    isMobileWeb,
    isTablet,
    onEnterLanding,
    onChangeLanding,
    AppContainer,
    errorLoading,
    onEnterBackstage,
    onEnterCuratorBSP,
    onLeaveCuratorBSP,
    isCuratorExperienceEnabled,
    AsyncDomain,
    onEnterPodcastBSP,
    onEnterPlaylist,
    onLeavePlaylist,
    createConditionalComponent,
    redirectPlaybackToBackstage,
    redirectIfAuthenticated,
    redirectIfAuthenticatedOnChange,
    redirectIfAuthenticatedOrBlocked,
    isLoggedInUser,
    onEnterUpgradeFlow,
    onChangeUpgradeFlow,
    onEnterMobileAccountWelcome,
    onEnterSettings,
    onEnterBackstageLyrics,
    onEnterSubscriptionManagement,
    isResponsiveSubscriptionEnabled,
    onEnterFamilyPlanManagement,
    redirectToPartnerUpgrade,
    onEnterPartnerUpgradeFlow,
    onEnterMobileMessage,
    onEnterVerification,
    onEnterPlans,
    onEnterPaypalCancel,
    onEnterPaypalComplete,
    onEnterBrowse,
  });
  return {
    childRoutes: mobileWebRoutes,
  };
}

export default {
  webRouteBuilder,
  mobileRouteBuilder,
};
